import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';

interface SuperAdminPasswordGateProps {
  children: React.ReactNode;
}

const SuperAdminPasswordGate: React.FC<SuperAdminPasswordGateProps> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const storageKey = 'super_admin_sensitive_pages_auth';

  const [authorized, setAuthorized] = useState<boolean>(() => {
    return sessionStorage.getItem(storageKey) === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user?.role !== 'SUPER_ADMIN') {
    return <>{children}</>;
  }

  const handleVerify = async () => {
    if (!password.trim()) {
      setError('Please enter password');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await authService.verifySensitivePagePassword(password);
      sessionStorage.setItem(storageKey, 'true');
      setAuthorized(true);
      setPassword('');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Invalid security key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings', { replace: true });
  };

  if (authorized) {
    return <>{children}</>;
  }

  return (
    <>
      <Dialog open maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle>Security Verification Required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your security key to access billing and invoice pages.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Security Key"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleVerify();
              }
            }}
            autoFocus
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleVerify} variant="contained" disabled={submitting}>
            {submitting ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SuperAdminPasswordGate;
